import { IInput } from "./Input";
import { SimpleSignal } from "../SimpleSignal";
import { ObjectUtils } from "../data/ObjectUtils";

export interface INumberInput extends IInput
{
	value:number;
	min:number;
	max:number;
}

export interface IBaseNumberInputConfig
{
	id?:string;
	value?:number;
	min?:number;
	max?:number;
	precision?:number;
	step?:number;
	units?:string;
	className?:string;
	type?:string;
	tabIndex?:number;
}

export interface INumberInputConfig extends IBaseNumberInputConfig
{
	scroll?:boolean;
	drag?:boolean;
}

export class NumberInput implements INumberInput
{
	public static defaultConfig:INumberInputConfig = {
		id: "",
		min: -Infinity,
		max: Infinity,
		precision: 0,
		step: 1,
		className: "utils-input"
	};

	public changeSignal:SimpleSignal;

	private _wrapper:HTMLElement;
	private _input:HTMLInputElement;

	private _config:INumberInputConfig;

	private _id:string;
	private _value:number;
	private _min = -Infinity;
	private _max = Infinity;
	private _precision = 0;
	private _step = 1;

	private _distance:number;
	private _mouseDown:number[] = [];
	private _mouseDownValue:number;

	constructor(config:INumberInputConfig)
	{
		config = ObjectUtils.mergeConfig(NumberInput.defaultConfig, config);
		this._config = config;

		this._input = document.createElement("input");
		if (config.type)
		{
			this._input.type = config.type; // "number"

			this._wrapper = document.createElement("div");
			this._wrapper.classList.add("numberInputWrapper");
			this._wrapper.classList.add("hbox");

			const arrowLeft = document.createElement("div");
			arrowLeft.classList.add("leftArrow");
			arrowLeft.style.cursor = "pointer";
			arrowLeft.addEventListener("click", this.onDecreaseClick);
			const arrowRight = document.createElement("div");
			arrowRight.classList.add("rightArrow");
			arrowRight.style.cursor = "pointer";
			arrowRight.addEventListener("click", this.onIncreaseClick);
			this._wrapper.appendChild(arrowLeft);
			this._wrapper.appendChild(this._input);
			this._wrapper.appendChild(arrowRight);
		}

		this._input.className = config.className;

		if (this._config.tabIndex)
		{
			this._input.setAttribute("tabIndex", <string><any>this._config.tabIndex);
		}

		this._input.addEventListener("change", this.onChange);
		this._input.addEventListener("focus", this.onFocus);
		this._input.addEventListener("blur", this.onBlur);

		this.enableMouseMoveScroll();

		// enable mouse move
		// scroll
		// touch

		this._id        = config.id;
		this._min       = config.min;
		this._max       = config.max;
		this._precision = config.precision;
		this._step      = config.step;

		if (!isNaN(config.value))
		{
			this.value = config.value;
		}

		this.changeSignal = new SimpleSignal();
	}

	/**
	 * Runs for user interaction.
	 * @param event
	 */
	private onChange = (event:Event) =>
	{
		var value = parseFloat(this._input.value);

		if (this.updateValue(value))
		{
			this.changeSignal.dispatch(this._value, this);
		}
	};

	private onIncreaseClick = () =>
	{
		const newValue = parseFloat(this._input.value) + 1;

		if (this.updateValue(newValue))
		{
			this.changeSignal.dispatch(newValue, this);

		}
	};

	private onDecreaseClick = () =>
	{
		const newValue = parseFloat(this._input.value) - 1;

		if (this.updateValue(newValue))
		{
			this.changeSignal.dispatch(newValue, this);
		}
	};

	private onFocus = (event:FocusEvent) =>
	{

	};

	private onBlur = (event:FocusEvent) =>
	{

	};

	public enableMouseMoveScroll()
	{
		this._input.addEventListener("mousedown", this.onMouseDown);
	}

	private onMouseDown = (event:MouseEvent) =>
	{
		//event.preventDefault();
		//this._distance = 0;
		this._mouseDownValue = this._value;

		this._mouseDown[0] = event.clientX;
		this._mouseDown[1] = event.clientY;

		//document.addEventListener("mousemove", this.onMouseMove);
		document.addEventListener("mouseup", this.onMouseUp);
	};

	private onMouseMove = (event:MouseEvent) =>
	{
		event.preventDefault();
		var dx = event['movementX'] || event['webkitMovementX'] || event['mozMovementX'] || 0;
		var dy = event['movementY'] || event['webkitMovementY'] || event['mozMovementY'] || 0;

		this._distance += dx - dy;

		var value = this._mouseDownValue + this._distance / (Math.pow(10, this._precision)) * this._step;

		if (this.updateValue(value))
		{
			this.changeSignal.dispatch(this._value, this);
		}
	};

	private onMouseUp = (e:MouseEvent) =>
	{
		//document.removeEventListener('mousemove', this.onMouseMove);
		document.removeEventListener('mouseup',  this.onMouseUp);

		// number type has no selectionEnd
		if (!this._config.type)
		{
			if (this._input.selectionEnd - this._input.selectionStart === 0)
			{
				// has no selection
				this._input.focus();
				this._input.select();
			}
			else
			{
				// has some selection -> let the user edit it
				//this._input.blur();
			}
		}
		else
		{
			this._input.select();
		}
	};

	public enable()
	{
		this._input.disabled = false;
	}

	public disable()
	{
		this._input.disabled = true;
	}

	/**
	 * @param value NaN or valid number
	 * @returns {boolean} True if the value was changed or false if not.
	 */
	private updateValue(value)
	{
		if (!isNaN(value))
		{
			value = this.clampValue(value);

			if (this._value !== value)
			{
				this._value = value;
				this._input.value = value.toFixed(this._precision);

				return true;
			}
		}

		// value is NaN or hasn't changed -> use previous value
		this._input.value = this._value.toFixed(this._precision);

		return false;
	}

	private clampValue(value)
	{
		return value < this._min ? this._min : value > this._max ? this._max : value;
	}

	/**
	 * @param value Either a valid number or NaN (when user inputs a string)
	 */
	public set value(value:number)
	{
		this.updateValue(value);
	}

	public get value()
	{
		return this._value;
	}

	public get min()
	{
		return this._min;
	}

	public get max()
	{
		return this._max;
	}

	public get id()
	{
		return this._id;
	}

	public get element():HTMLElement
	{
		return this._wrapper || this._input;
	}
}
